import { createSelector } from "@ngrx/store";

import { environment } from "src/environments/environment";

import { hasOpenBlueSagePayLink } from "../../bluesage/bluesage.util";
import { CLOSE_JOB_ONGOING_EVENT_STATUSES } from "../../global.constants";

import { formatEventDetails } from "../../job/job-tool.reducer";
import { jobsFeature } from "../../jobs/jobs.reducer";
import { createCheckSinglePermissionSelector } from "../../state/branding.store";

import { cancelAndCloseDialogFeature } from "./cancel-and-close-dialog.reducer";

export const selectCancelAndCloseJob = createSelector(
  cancelAndCloseDialogFeature.selectJobId,
  jobsFeature.selectJobs,
  (jobId, jobs) => jobs[jobId],
);

export const selectCancelAndCloseJobFranchiseId = createSelector(
  selectCancelAndCloseJob,
  (job) => {
    const isArea = job?.zone?.type === 'area';
    return isArea ? job?.zone?.parent?.id : job?.zone?.id;
  },
);

export const selectCancelAndClosePendingTransactions = createSelector(
  selectCancelAndCloseJob,
  (job) => {
    const pending = (job?.transactions || []).filter((t) => t.stage === 'pending' && !t.deletedAt);
    const payLinkCount = pending.filter((t) => hasOpenBlueSagePayLink(t)).length;

    return {
      payLinkCount,
      hasOtherPending: pending.length > payLinkCount,
    };
  },
);

export const selectCancelAndCloseBlocker = createSelector(
  selectCancelAndClosePendingTransactions,
  createCheckSinglePermissionSelector({ permission: 'bluesage.cancelLink' }),
  ({ payLinkCount, hasOtherPending }, mayCancelPayLinks) => {
    if (hasOtherPending) return 'pendingTransactions' as const;
    if (payLinkCount > 0 && !mayCancelPayLinks) return 'payLinkPermission' as const;
    return undefined;
  },
);

export const selectBookedEvents = createSelector(
  selectCancelAndCloseJob,
  cancelAndCloseDialogFeature.selectEventsToCancel,
  (job, eventsToCancel) => {
    if (!job) return [];

    const timezone = job.zone?.timezone ?? environment.defaultTimezone;

    return (job.events || [])
      .filter((event) => CLOSE_JOB_ONGOING_EVENT_STATUSES.includes(event.status))
      .map((event) => {
        const formatted = formatEventDetails(event, timezone);

        return {
          id: event.id,
          title: formatted.title,
          details: formatted.details,
          selected: eventsToCancel[event.id] === true,
        };
      });
  },
);
